import { Download } from 'lucide-react';
import { Button, type ButtonProps } from './components/Button.js';
import { useInstallPrompt } from './useInstallPrompt.js';

export interface InstallButtonProps
  extends Omit<ButtonProps, 'onClick' | 'children' | 'type'> {
  /** Optional override label (pre-translated string). Defaults to "Install S4K". */
  readonly label?: string;
  /**
   * Optional pre-resolved label string passed by the consumer's i18n layer.
   * If both `label` and `labelKey` are provided, `label` wins.
   *
   * The `@chessmate/ui` package is rendering-agnostic and does NOT resolve keys
   * itself — consumers wire up `react-i18next` and pass the resolved string
   * via this prop.
   */
  readonly labelKey?: string;
}

/**
 * Renders nothing until the browser fires `beforeinstallprompt`, then shows a
 * "Install S4K" CTA that defers and triggers the install dialog. Hides again
 * after install (or when the app is already running in standalone mode).
 *
 * SSR-safe: defaults to hidden, hydrates without mismatch, upgrades after mount.
 */
export function InstallButton({ className = '', label, labelKey, ...rest }: InstallButtonProps) {
  const resolvedLabel = label ?? labelKey ?? 'Install S4K';
  const { isInstallable, install } = useInstallPrompt();
  if (!isInstallable) return null;
  return (
    <Button
      type="button"
      variant="secondary"
      data-testid="install-s4k"
      onClick={() => void install()}
      className={className}
      {...rest}
    >
      <Download className="mr-1 inline" size={16} />
      {resolvedLabel}
    </Button>
  );
}